/**
 * src/pages/WhereDidWeComeFrom/sections/StardustExplorer.tsx
 * Section 3: "Stardust" Element-Origin Explorer
 * Interactive periodic-table grid with element origin classification,
 * filter chips, and accessible telemetry detail panel.
 */

import React, { useState } from "react";
import { Sparkles, Info, Filter } from "lucide-react";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Footnote } from "@/components/originBeyond/Footnote";
import { PERIODIC_ELEMENTS, ELEMENT_ORIGIN_CATEGORIES, type ElementOriginId, type ChemicalElement } from "../whereDidWeComeFromData";
import "@/components/cosmic/cosmic.css";
import styles from "../WhereDidWeComeFrom.module.css";
export function StardustExplorer() {
  const [selectedOrigin, setSelectedOrigin] = useState<ElementOriginId | "all">("all");
  const [selectedZ, setSelectedZ] = useState<number>(6); // Default to Carbon (Z=6)

  const activeElement: ChemicalElement = PERIODIC_ELEMENTS.find(el => el.z === selectedZ) || PERIODIC_ELEMENTS[0];
  const filteredElements = selectedOrigin === "all" ? PERIODIC_ELEMENTS : PERIODIC_ELEMENTS.filter(el => el.origin === selectedOrigin);
  return <section id="stardust-explorer" className="w-full max-w-[76rem] mx-auto px-4 sm:px-6 lg:px-8 min-w-0" aria-label="Stardust Element Origin Explorer">
      <SectionHeading eyebrow="Cosmochemistry Telemetry" title="Stardust: The Astrophysical Origin of the Elements" description="Every atom in your blood, bones, and breath was forged in an astrophysical forge. Explore where the chemical elements originated across cosmic time." />

      {/* Intro note referencing B2FH and GW170817 */}
      <div className="ql-glass p-5 sm:p-6 mb-8 border border-border/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Sparkles className="size-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed font-sans">
            First formulated comprehensively in the landmark 1957 B²FH paper
            <Footnote n={2} />, our understanding was dramatically verified in 2017 when gravitational wave detectors and optical telescopes witnessed two colliding neutron stars synthesize immense clouds of gold, platinum, and iodine
            <Footnote n={3} />. The exact proportion of heavy elements produced by mergers versus rare magnetorotational supernovae remains an active research area.
          </p>
        </div>
      </div>

      {/* Filter Chips Bar */}
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
          <Filter className="size-3.5 text-primary" aria-hidden="true" />
          <span className="uppercase tracking-wider">Filter by Astrophysical Forge:</span>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setSelectedOrigin("all")} className="ql-chip" data-active={selectedOrigin === "all"}>
            All Forges ({PERIODIC_ELEMENTS.length})
          </button>
          {Object.values(ELEMENT_ORIGIN_CATEGORIES).map(cat => {
          const count = PERIODIC_ELEMENTS.filter(el => el.origin === cat.id).length;
          return <button key={cat.id} type="button" onClick={() => setSelectedOrigin(cat.id)} className="ql-chip" data-active={selectedOrigin === cat.id}>
                <span>{cat.short}</span>
                <span className="text-[11px] opacity-75">({count})</span>
              </button>;
        })}
        </div>
      </div>

      {/* Grid + Detail Split Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start min-w-0">
        {/* Periodic Grid Container */}
        <div className="lg:col-span-8 ql-glass p-4 sm:p-6 overflow-x-auto min-w-0">
          <div className="flex items-center justify-between pb-3 mb-3 border-b border-border/40 text-xs font-mono text-muted-foreground">
            <span>Select an element cell to view cosmic telemetry</span>
            <span className="hidden sm:inline">Periodic Blocks Z=1 to Z=92</span>
          </div>

          <div className={styles.ptableGrid} role="grid" aria-label="Periodic Table of Elements by Origin">
            {PERIODIC_ELEMENTS.map(el => {
            const originDef = ELEMENT_ORIGIN_CATEGORIES[el.origin];
            const isSelected = el.z === selectedZ;
            const isDimmed = selectedOrigin !== "all" && el.origin !== selectedOrigin;
            return <button key={el.z} type="button" onClick={() => setSelectedZ(el.z)} className={`${styles.elementCell} ${isSelected ? styles.elementCellActive : ""} ${isDimmed ? "opacity-20 pointer-events-none" : ""}`} style={{
              gridRow: el.row,
              gridColumn: el.col
            }} aria-selected={isSelected} title={`${el.name} (Z=${el.z}) - ${originDef.label}`}>
                  <span className="text-[9px] font-mono leading-none text-muted-foreground">
                    {el.z}
                  </span>
                  <span className="text-xs sm:text-sm font-bold font-serif leading-tight text-foreground">
                    {el.symbol}
                  </span>
                  <span className={`size-1 rounded-full mt-0.5 ${el.origin === "bigbang" ? "bg-cyan-400" : el.origin === "dyingstars" ? "bg-amber-400" : el.origin === "supernovae" ? "bg-emerald-400" : el.origin === "mergers" ? "bg-purple-400" : el.origin === "cosmicrays" ? "bg-blue-400" : "bg-rose-400"}`} />
                </button>;
          })}
          </div>

          {/* Legend Color Swatches */}
          <div className="mt-6 pt-4 border-t border-border/30 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs font-mono text-muted-foreground">
            {Object.values(ELEMENT_ORIGIN_CATEGORIES).map(cat => <div key={cat.id} className="flex items-center gap-2">
                <span className={`size-2.5 rounded-sm ${cat.id === "bigbang" ? "bg-cyan-400" : cat.id === "dyingstars" ? "bg-amber-400" : cat.id === "supernovae" ? "bg-emerald-400" : cat.id === "mergers" ? "bg-purple-400" : cat.id === "cosmicrays" ? "bg-blue-400" : "bg-rose-400"}`} />
                <span className="truncate">{cat.short}</span>
              </div>)}
          </div>
        </div>

        {/* Selected Element Telemetry Card */}
        <div className="lg:col-span-4 ql-glass-strong p-6 sm:p-7 rounded-xl border border-primary/30 flex flex-col gap-5 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
                Atomic Number Z = {activeElement.z}
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl font-extrabold text-foreground mt-1">
                {activeElement.name}{" "}
                <span className="text-primary font-mono text-xl sm:text-2xl">
                  [{activeElement.symbol}]
                </span>
              </h3>
            </div>
            <div className="size-14 rounded-xl border border-primary/40 bg-card/60 flex flex-col items-center justify-center font-mono">
              <span className="text-[10px] text-muted-foreground">Symbol</span>
              <span className="text-lg font-bold text-primary">{activeElement.symbol}</span>
            </div>
          </div>

          {/* Primary Forge Badge */}
          <div className="p-3 rounded-lg border border-border/50 bg-card/40 flex flex-col gap-1.5">
            <span className="text-xs font-mono text-muted-foreground uppercase">
              Primary Astrophysical Forge:
            </span>
            <div className="flex items-center gap-2">
              <span className={`size-2.5 rounded-full ${activeElement.origin === "bigbang" ? "bg-cyan-400 shadow-[0_0_8px_var(--glow-primary)]" : activeElement.origin === "dyingstars" ? "bg-amber-400 shadow-[0_0_8px_hsl(38,95%,55%)]" : activeElement.origin === "supernovae" ? "bg-emerald-400 shadow-[0_0_8px_hsl(150,90%,50%)]" : activeElement.origin === "mergers" ? "bg-purple-400 shadow-[0_0_8px_var(--glow-violet)]" : activeElement.origin === "cosmicrays" ? "bg-blue-400 shadow-[0_0_8px_var(--glow-laser)]" : "bg-rose-400"}`} />
              <span className="font-serif font-bold text-sm sm:text-base text-foreground">
                {ELEMENT_ORIGIN_CATEGORIES[activeElement.origin].label}
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed font-sans mt-1">
              {ELEMENT_ORIGIN_CATEGORIES[activeElement.origin].description}
            </p>
          </div>

          {/* Notable Astrophysical Fact */}
          {activeElement.notableFact && <div className="flex flex-col gap-1">
              <span className="text-xs font-mono text-primary font-semibold uppercase flex items-center gap-1.5">
                <Info className="size-3.5" aria-hidden="true" />
                <span>Cosmic Insight</span>
              </span>
              <p className="text-xs sm:text-sm text-foreground leading-relaxed font-sans">
                {activeElement.notableFact}
              </p>
            </div>}

          {/* Biological Role */}
          {activeElement.biologicalRole && <div className="pt-3 border-t border-border/40 flex flex-col gap-1">
              <span className="text-xs font-mono text-muted-foreground uppercase">
                Terrestrial / Biological Function:
              </span>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed font-sans">
                {activeElement.biologicalRole}
              </p>
            </div>}
        </div>
      </div>
    </section>;
}